import type { BasicCustomerData, Permission } from "@/services/model";
import { isBusinessCustomer } from "@/utils/customerUtils";

/** Current consent type returned by getCustomer for magic link login. */
export const MAGIC_LINK_PERMISSION_TYPE = "MAGIC_LINK";

const customDataFlagKeys = [
  "magicLoginAllowed",
  "magicLoginEnabled",
  "allowMagicLogin",
] as const;

export const findMagicLoginPermission = (
  permissions?: Permission[],
): Permission | undefined =>
  permissions?.find(
    ({ permissionType }) =>
      String(permissionType) === MAGIC_LINK_PERMISSION_TYPE,
  );

const readCustomDataFlag = (
  customData: BasicCustomerData["customData"],
): boolean | undefined => {
  if (!customData || typeof customData !== "object") {
    return undefined;
  }

  const record = customData as Record<string, unknown>;

  for (const key of customDataFlagKeys) {
    if (typeof record[key] === "boolean") {
      return record[key];
    }
  }

  return undefined;
};

/**
 * Resolves whether magic-link login is enabled for the signed-in customer.
 * Falls back to account-type defaults (private: on, business: off) when BE omits the flag.
 */
export const getMagicLoginEnabled = (customer?: BasicCustomerData): boolean => {
  if (!customer) {
    return false;
  }

  const permission = findMagicLoginPermission(customer.permissions);

  if (permission !== undefined) {
    return !!permission.accepted;
  }

  const customDataFlag = readCustomDataFlag(customer.customData);

  if (customDataFlag !== undefined) {
    return customDataFlag;
  }

  return !isBusinessCustomer(customer);
};
